import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Column,
  FormController,
  MuiBox,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
} from "../../../../../../../../components";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../../../helpers/constants";
import {
  getFormattedAmount,
  isEmpty,
  isValueIncludedIgnoreCase,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  FeesAndExpenseRow,
  ProgramInvoice,
  RaiseInvoiceFormKey,
  RaiseInvoiceFormType,
} from "../../../../../../modelsAndSchemas/Models";
import { raiseInvoiceSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.RAISE_INVOICE}`;

type RaiseInvoiceListProps = {
  invoiceFeesAndExpense: FeesAndExpenseRow[];
  currencyName: string;
};

export const RaiseInvoiceList: FC<RaiseInvoiceListProps> = ({
  invoiceFeesAndExpense,
  currencyName,
}) => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const getLabel = (key: keyof typeof raiseInvoiceSiteCoreKeys) => {
    return fetchLabel(
      raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
      raiseInvoiceSiteCoreKeys[key],
    );
  };
  const { control } = useForm<Partial<ProgramInvoice>>();

  const [state, setState] = useState<{
    filteredList: RaiseInvoiceFormType[];
    originalList: RaiseInvoiceFormType[];
  }>({
    filteredList: [],
    originalList: [],
  });

  const uniqueTypes = Array.from(
    new Set(state.originalList.map((item) => item.type)),
  );

  useEffect(() => {
    let transformedData: RaiseInvoiceFormType[] = [];

    invoiceFeesAndExpense.forEach((element) => {
      const {
        name: feeOrExpenseName,
        typeName: type,
        expenseCategoryName: category,
        billingAmount,
        tax: taxAmount,
        feeOrExpenseType,
      } = element;

      transformedData.push({
        feeOrExpenseName: feeOrExpenseName,
        type: type,
        category: category,
        billingAmount: String(billingAmount || 0),
        taxAmount: String(taxAmount || 0),
        feeOrExpenseType: feeOrExpenseType,
        totalInvoiceAmount: String(
          Number(billingAmount || 0) + Number(taxAmount || 0),
        ),
        lineNumber: (transformedData.length + 1).toString(),
      });
    });

    setState((prevState) => ({
      ...prevState,
      originalList: transformedData,
      filteredList: transformedData,
    }));
  }, [invoiceFeesAndExpense]);

  const filters = useWatch({ control });

  useEffect(() => {
    appliedFilters(state.originalList);
  }, [filters]);

  const appliedFilters = (invoiceData: RaiseInvoiceFormType[] = []) => {
    if (isEmpty(invoiceData)) return;
    let filteredList = [...invoiceData];

    const {
      billingAmount,
      category,
      feeOrExpenseName,
      feeOrExpenseType,
      taxAmount,
      totalInvoiceAmount,
      type,
      lineNumber,
    } = filters || {};

    filteredList = filteredList.filter((item) => {
      return (
        isValueIncludedIgnoreCase(item.billingAmount, billingAmount) &&
        isValueIncludedIgnoreCase(item.category, category) &&
        isValueIncludedIgnoreCase(item.feeOrExpenseName, feeOrExpenseName) &&
        isValueIncludedIgnoreCase(item.feeOrExpenseType, feeOrExpenseType) &&
        isValueIncludedIgnoreCase(item.taxAmount, taxAmount) &&
        isValueIncludedIgnoreCase(item.type, type) &&
        isValueIncludedIgnoreCase(
          item.totalInvoiceAmount,
          totalInvoiceAmount,
        ) &&
        isValueIncludedIgnoreCase(item.lineNumber, lineNumber)
      );
    });

    setState((prevState: any) => ({
      ...prevState,
      filteredList,
    }));
    tableRef.current?.resetPagination();
  };

  const getAmountWithCurrency = (
    amount: string | number,
    currencyName: string,
  ) => {
    if (!amount || isNaN(Number(amount))) return EMPTY_VALUE_DASH;
    return `${currencyName} ${getFormattedAmount(Number(amount))}`;
  };

  const columns: Column[] = [
    {
      id: RaiseInvoiceFormKey.lineNumber,
      label: getLabel("invoiceLineNumberTableColumn"),
      isSortVisible: true,
      render: (row: any, index?: number) => {
        return <MuiTypography>{row.lineNumber}</MuiTypography>;
      },
    },
    {
      id: RaiseInvoiceFormKey.feeOrExpenseName,
      label: getLabel("feeOrExpenseNameTableColumn"),
      isSortVisible: true,
    },
    {
      id: RaiseInvoiceFormKey.type,
      label: getLabel("typeTableColumn"),
      isSortVisible: true,
      minWidth: 200,
    },
    {
      id: RaiseInvoiceFormKey.category,
      label: getLabel("categoryTableColumn"),
      isSortVisible: true,
      minWidth: 200,
    },
    {
      id: RaiseInvoiceFormKey.feeOrExpenseType,
      label: getLabel("feeOrExpenseTypeTableColumn"),
      isSortVisible: true,
    },
    {
      id: RaiseInvoiceFormKey.billingAmount,
      label: getLabel("billingAmountTableColumn"),
      isSortVisible: true,
      render: (row: any) =>
        getAmountWithCurrency(row.billingAmount, currencyName),
    },
    {
      id: RaiseInvoiceFormKey.taxAmount,
      label: getLabel("taxAmountTableColumn"),
      isSortVisible: true,
      render: (row: any) => getAmountWithCurrency(row.taxAmount, currencyName),
    },
    {
      id: RaiseInvoiceFormKey.totalInvoiceAmount,
      label: getLabel("totalInvoiceAmountTableColumn"),
      isSortVisible: true,
      render: (row: any) =>
        getAmountWithCurrency(row.totalInvoiceAmount, currencyName),
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.lineNumber}
      key={RaiseInvoiceFormKey.lineNumber}
    >
      <MuiTextField
        id={`${pageId}-${RaiseInvoiceFormKey.lineNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.lineNumber}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.feeOrExpenseName}
      key={RaiseInvoiceFormKey.feeOrExpenseName}
    >
      <MuiTextField
        id={`${pageId}-${RaiseInvoiceFormKey.feeOrExpenseName}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.feeOrExpenseName}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.type}
      key={RaiseInvoiceFormKey.type}
    >
      <MuiSelect
        label=""
        options={uniqueTypes.map((type) => ({ label: type, value: type }))}
        visibleDefaultSelectOption={true}
        value={filters.type}
        id={`${pageId}-${RaiseInvoiceFormKey.type}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.category}
      key={RaiseInvoiceFormKey.category}
    >
      <MuiTextField
        id={`${pageId}-${RaiseInvoiceFormKey.category}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.category}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.feeOrExpenseType}
      key={RaiseInvoiceFormKey.feeOrExpenseType}
    >
      <MuiTextField
        id={`${pageId}-${RaiseInvoiceFormKey.feeOrExpenseType}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.feeOrExpenseType}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.billingAmount}
      key={RaiseInvoiceFormKey.billingAmount}
    >
      <MuiTextField
        id={`${pageId}-${RaiseInvoiceFormKey.billingAmount}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.billingAmount}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.taxAmount}
      key={RaiseInvoiceFormKey.taxAmount}
    >
      <MuiTextField
        id={`${pageId}-${RaiseInvoiceFormKey.taxAmount}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.taxAmount}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={RaiseInvoiceFormKey.totalInvoiceAmount}
      key={RaiseInvoiceFormKey.totalInvoiceAmount}
    >
      <MuiTextField
        id={`${pageId}-${RaiseInvoiceFormKey.totalInvoiceAmount}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.totalInvoiceAmount}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  return (
    <MuiBox>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={
          state.filteredList as unknown as Record<string, ReactNode>[]
        }
        noDataInApi={state.originalList.length === 0}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
    </MuiBox>
  );
};
